/**
 * dashboard/quizzes/$id.preview.tsx — Tutor quiz preview (Phase G.2).
 *
 * Read-only student-eye view of a quiz. Data diambil dari
 * GET /api/quiz/mine/:id (quiz milik sendiri) dengan fallback ke
 * GET /api/quiz/library/:id (v0.1.4 — item library di halaman quiz
 * sekarang clickable dan mendarat di halaman ini). Renders questions
 * in student style:
 *   - prompt
 *   - options list (no answer indicators — tutor must mentally
 *     check against the editor)
 *   - no edit/add/remove controls
 *
 * Useful for tutors to double-check their quiz reads correctly
 * before activating for a class.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { createFileRoute, redirect, useParams } from '@tanstack/react-router';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import { freshSessionQuery } from '../../../lib/api/queries.js';
import {
  LEARNING_LEVEL_LABEL,
  toLearningLevelEnum,
} from '../../../lib/leveling.js';

function levelBadge(sdGrade: number): string {
  const lv = toLearningLevelEnum(sdGrade);
  return lv ? `${lv} ${LEARNING_LEVEL_LABEL[lv]}` : '—';
}

export const Route = createFileRoute('/dashboard/quizzes/$id_/preview')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({ to: '/login', search: { error: undefined, redirect: location.href } });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/' });
    }
    return null;
  },
  component: TutorQuizPreviewPage,
});

interface ApiQuiz {
  id: string;
  title: string;
  description: string | null;
  sd_grade: number;
  source: 'library' | 'custom' | 'library_pending';
  questions: Array<{
    id: string;
    type: 'single_choice' | 'true_false' | 'multi_select';
    difficulty: 'easy' | 'medium' | 'hard';
    prompt: string;
    options_json: string | null;
    points: number;
  }>;
}

const TYPE_LABEL: Record<ApiQuiz['questions'][number]['type'], string> = {
  single_choice: 'Pilihan ganda',
  true_false: 'Benar / Salah',
  multi_select: 'Pilihan banyak',
};

function TutorQuizPreviewPage(): ReactNode {
  const { id } = useParams({ strict: false }) as { id: string };
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [quiz, setQuiz] = useState<ApiQuiz | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Coba endpoint "milik sendiri" dulu; kalau 403/404 (quiz
        // library milik tutor/admin lain), fallback ke endpoint
        // library read-only.
        let r = await fetch(`/api/quiz/mine/${encodeURIComponent(id)}`, { credentials: 'include' });
        let j = (await r.json()) as { ok: boolean; data?: ApiQuiz; error?: { message: string } };
        if (!j.ok || !j.data) {
          r = await fetch(`/api/quiz/library/${encodeURIComponent(id)}`, { credentials: 'include' });
          j = (await r.json()) as { ok: boolean; data?: ApiQuiz; error?: { message: string } };
        }
        if (cancelled) return;
        if (!j.ok || !j.data) {
          setLoadError(j.error?.message ?? 'Gagal memuat quiz.');
          setLoading(false);
          return;
        }
        setQuiz(j.data);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError((err as Error).message);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Quiz Tutor" title="Memuat preview…" />
      </main>
    );
  }
  if (loadError || !quiz) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Quiz Tutor" title="Quiz tidak ditemukan" />
        <StatePanel variant="error" title="Gagal memuat quiz" description={loadError ?? 'Tidak ditemukan'} />
      </main>
    );
  }

  const totalPoints = quiz.questions.reduce((s, q) => s + q.points, 0);

  return (
    <main className="page-shell" data-testid="quiz-preview">
      <PageHeader
        eyebrow={`Preview · ${levelBadge(quiz.sd_grade)}`}
        title={quiz.title}
        description={
          (quiz.description ? `${quiz.description} · ` : '') +
          `${quiz.questions.length} soal · max ${totalPoints}⭐`
        }
      />

      <div className="form-message form-message-muted">
        Ini adalah tampilan siswa — jawaban benar tidak ditampilkan di sini.
      </div>

      <section className="section">
        <ol className="preview-question-list" data-testid="preview-questions">
          {quiz.questions.map((q, i) => {
            const options = q.options_json ? (JSON.parse(q.options_json) as string[]) : null;
            return (
              <li key={q.id} className="card question-editor preview-question">
                <header className="question-editor-header">
                  <h4 className="question-editor-title">
                    Soal {i + 1} · {TYPE_LABEL[q.type]}
                  </h4>
                  <span className="card-meta">{q.points}⭐</span>
                </header>
                <p className="preview-prompt">{q.prompt}</p>
                {q.type === 'true_false' ? (
                  <ul className="preview-options">
                    <li className="preview-option">Benar</li>
                    <li className="preview-option">Salah</li>
                  </ul>
                ) : (
                  <ul className="preview-options">
                    {options?.map((opt, j) => (
                      <li key={j} className="preview-option">
                        <span className="preview-option-marker">{String.fromCharCode(65 + j)}.</span>
                        <span>{opt}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      </section>
    </main>
  );
}
